import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';

import { apisServiceFunction } from '@mygpt/app/functions/services/list';
import { apisStoreFunction } from '@mygpt/app/functions/store';

export const useAPIsListFunction = (datasetId: string, options: { defaultPageSize?: number } = {}) => {
  const [loading, setLoading] = React.useState(true);
  const { defaultPageSize = 10 } = options;
  const store = useStore(apisStoreFunction);
  const data = useMemo(() => store.records[datasetId] ?? [], [store.records, datasetId]);
  const [page, setPage] = React.useState<{ page: number; total: number; size: number; pages: number }>({
    page: 1,
    total: data.length,
    pages: 1,
    size: defaultPageSize,
  });
  const totalRef = useRef<number>(0);

  useLayoutEffect(() => {
    setPage((prev) => {
      const total = (store.cacheTotal[datasetId] ?? 0) + totalRef.current;
      return {
        ...prev,
        total,
        pages: Math.ceil(total / prev.size),
      };
    });
  }, [datasetId, store.cacheTotal]);

  const loadList = React.useCallback(
    async (params: Omit<Parameters<typeof apisServiceFunction>[0], 'datasetId'>) => {
      setLoading(true);
      return apisServiceFunction({ datasetId, size: defaultPageSize, ...params })
        .then((res) => {
          apisStoreFunction.setState({
            records: {
              [datasetId]: res.data.items,
            },
            cacheTotal: {
              [datasetId]: 0,
            },
          });
          setPage({
            page: res.data.page,
            total: res.data.total,
            pages: res.data.pages,
            size: res.data.size,
          });
          totalRef.current = res.data.total;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId, defaultPageSize]
  );

  const handlePageChange = React.useCallback(
    (options: Partial<{ page: number; size: number }>) => setPage((prev) => ({ ...prev, ...options })),
    []
  );

  useLayoutEffect(() => {
    if (datasetId) loadList({ page: page.page, size: page.size });
    else setLoading(false);
  }, [datasetId, loadList, page.page, page.size]);

  return {
    loading,
    data,
    page,
    setPage: handlePageChange,
    loadList,
  };
};
